/**
 * Viewer WS API (F2) — receive-only, no auth.
 *
 * Opens the public viewer downlink at `${VITE_WS_BASE}/ws/viewer/{token}` and
 * surfaces inbound EventEnvelopeV1 events. Unlike the host uplink, the viewer
 * never sends audio: this is a pure downlink. It reuses the shared `WsClient`
 * for reconnect/backoff, and treats close code 4404 as "link invalid or
 * expired" — a terminal state that must NOT trigger a reconnect.
 */

import {
  WsClient,
  type WsStatus,
  type EventListener,
} from '@/shared/ws/wsClient';
import type { EventEnvelopeV1, EventType } from '@/shared/models/events';

/** WS close code signalling an invalid / expired / revoked viewer link. */
export const VIEWER_LINK_INVALID_CODE = 4404 as const;

export interface ViewerConnection {
  /** Subscribe to a single event type. Returns an unsubscribe function. */
  on: <K extends EventType>(type: K, listener: EventListener<K>) => () => void;
  /** Subscribe to every inbound event. Returns an unsubscribe function. */
  onEvent: (listener: (event: EventEnvelopeV1) => void) => () => void;
  /** Current socket status. */
  status: () => WsStatus;
  /** Close the connection and stop reconnecting. */
  close: () => void;
}

export interface OpenViewerOptions {
  onStatus?: (status: WsStatus) => void;
  /** Fired once when the server closes the link as invalid/expired (4404). */
  onLinkInvalid?: () => void;
  onError?: (error: Event) => void;
}

/**
 * Open a receive-only viewer connection for a link token.
 *
 * The token is path-encoded; the socket connects against `VITE_WS_BASE`
 * (handled inside `WsClient`). Call `.close()` when the page unmounts.
 */
export function openViewerConnection(
  token: string,
  options: OpenViewerOptions = {},
): ViewerConnection {
  const client = new WsClient({
    url: `/ws/viewer/${encodeURIComponent(token)}`,
    onStatus: options.onStatus,
    onError: options.onError,
    onClose: (code) => {
      if (code === VIEWER_LINK_INVALID_CODE) {
        options.onLinkInvalid?.();
        return false; // veto reconnect — the link will never become valid
      }
      return true;
    },
  });

  client.connect();

  return {
    on: (type, listener) => client.on(type, listener),
    onEvent: (listener) => client.onEvent(listener),
    status: () => client.currentStatus,
    close: () => client.close(),
  };
}
